import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Quote } from "lucide-react";
import { api, productImage } from "../../lib/api";
import { Stars } from "../../components/reviews/Stars";

const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "K";

function ReviewCard({ r }) {
  const photo = r.photos?.[0]?.url;
  return (
    <article className="snap-start shrink-0 w-[300px] md:w-[340px] bg-white border border-[#E8D9C3] p-5 flex flex-col" data-testid={`wedding-review-${r.review_id}`}>
      <div className="flex items-center gap-3">
        {photo ? (
          <img src={productImage(photo)} alt={`${r.product || "wedding stationery"} photographed by ${r.display_name}`} className="w-12 h-12 object-cover" loading="lazy" />
        ) : (
          <div className="w-12 h-12 bg-[#E8D9C3] text-[#2C3340] font-display font-black flex items-center justify-center">{initials(r.display_name)}</div>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-[#2C3340] leading-tight truncate">{r.display_name}</div>
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 truncate">{[r.city, r.product].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <Stars value={r.rating} size="w-3.5 h-3.5" className="mt-3" />
      <p className="text-sm text-slate-700 leading-relaxed mt-2 line-clamp-4">
        <Quote className="w-3.5 h-3.5 inline text-kolt-orange mr-1 -mt-1" />{r.comment}
      </p>
      {(r.order_ref || r.source) && (
        <div className="mt-auto pt-3 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-700">
          <BadgeCheck className="w-3.5 h-3.5" /> {r.order_ref ? "Verified order" : `Via ${r.source}`}
        </div>
      )}
    </article>
  );
}

export function WeddingReviewsStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reviews/public", { params: { segment: "wedding" } }).then(({ data }) => setData(data)).catch(() => setData({ enabled: false }));
  }, []);
  if (!data?.enabled || !data.reviews?.length) return null;
  return (
    <section className="bg-[#F2EADF] border-y border-[#2C3340]/10" data-testid="wedding-reviews-strip">
      <div className="container-page py-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-kolt-orange mb-1">Couples &amp; planners on our press</div>
            <h2 className="font-display font-black text-xl md:text-2xl tracking-tight text-[#2C3340] flex items-center gap-3">
              <Stars value={Math.round(data.average)} /> {data.average} / 5 <span className="text-slate-500 font-normal text-base">· {data.count} wedding review{data.count === 1 ? "" : "s"}</span>
            </h2>
          </div>
          <Link to="/reviews" className="font-mono text-[10px] tracking-[0.22em] uppercase underline text-[#2C3340]/70 hover:text-[#2C3340]" data-testid="wedding-reviews-all">All reviews →</Link>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x pb-2 scrollbar-none" data-testid="wedding-reviews-row">
          {data.reviews.slice(0, 8).map((r) => <ReviewCard key={r.review_id} r={r} />)}
        </div>
      </div>
    </section>
  );
}
